import { Bar, BarConfig } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import { GET_COUNT_BY_REGION, StatType } from '@frontend/data-access';
import { Card, DatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

const { RangePicker } = DatePicker;

const UsersByRegion = () => {
  const { t } = useTranslation('admin');
  const endDate = dayjs();
  const startDate = dayjs().subtract(3, 'month');
  const [date, setDate] = useState([
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD'),
  ]);
  const [getData, { data, loading }] = useLazyQuery<{
    userCountByRegion: StatType[];
  }>(GET_COUNT_BY_REGION);
  const [option, setOption] = useState({
    key: 'DESC',
    label: t('highest_to_lowest'),
  });

  const options = useMemo(
    () => [
      { key: 'DESC', label: t('highest_to_lowest') },
      { key: 'ASC', label: t('lowest_to_highest') },
    ],
    [t]
  );

  const highestRegion = useMemo(() => {
    let highest = 0;
    let region = '';
    for (const item of data?.userCountByRegion || []) {
      if (item?.count > highest) {
        highest = item.count;
        region = item.region;
      }
    }
    return region;
  }, [data]);

  const config: BarConfig = {
    data: data?.userCountByRegion || [],
    loading,
    xField: 'region',
    yField: 'count',
    color: ({ region }) => {
      if (region === highestRegion) {
        return '#47DA47';
      }
      return '#529FF0';
    },
    appendPadding: [0, 20, 0, 0],
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
      },
    },
    xAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
        },
      },
    },
    label: {
      text: 'count',
      position: 'right',
      textAlign: 'start',
      dx: 8,
      style: {
        fill: 'rgba(0, 0, 0, 0.65)',
        fontFamily: 'Lufga',
      },
    },
  };

  const handleChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    if (dates) {
      setDate([dateStrings[0], dateStrings[1]]);
    }
  };

  useEffect(() => {
    getData({
      variables: {
        order: option.key === 'DESC',
        startDate: date[0],
        endDate: date[1],
      },
    });
  }, [date, getData, option]);

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-900 font-medium">
            {t('users_by_region')}
          </div>
          <div>
            <RangePicker
              onChange={handleChange}
              defaultValue={[dayjs(date[0]), dayjs(date[1])]}
              style={{
                borderRadius: 20,
              }}
            />
          </div>
        </div>
        <Bar {...config} />
      </div>
    </Card>
  );
};

export default UsersByRegion;
